import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, Sparkles } from "lucide-react";
import { verifyPaymentReturn, useUnlock } from "@/lib/payments";

export default function PaymentReturn() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh, markUnlockedLocally } = useUnlock();
  const [status, setStatus] = useState("loading"); // loading | approved | pending | failure

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // MP appends: collection_status, status, payment_id, external_reference
    const mpStatus = params.get("status") || params.get("collection_status");
    const paymentId = params.get("payment_id") || params.get("collection_id");
    const orderId = params.get("external_reference");

    (async () => {
      if (!orderId) {
        setStatus("failure");
        return;
      }
      try {
        const data = await verifyPaymentReturn(orderId, paymentId || "");
        if (data.status === "completed") {
          markUnlockedLocally(data.plan || "basic");
          await refresh();
          setStatus("approved");
        } else if (mpStatus === "pending" || mpStatus === "in_process") {
          setStatus("pending");
        } else if (mpStatus === "approved") {
          // MP says approved but our webhook hasn't fired yet — brief retry
          setTimeout(async () => {
            const r = await verifyPaymentReturn(orderId, paymentId || "");
            if (r.status === "completed") {
              markUnlockedLocally(r.plan || "basic");
              await refresh();
              setStatus("approved");
            } else {
              setStatus("pending");
            }
          }, 1500);
        } else {
          setStatus("failure");
        }
      } catch {
        setStatus("failure");
      }
    })();
  }, []);

  return (
    <div className="pt-10 text-center max-w-lg mx-auto">
      {status === "loading" && (
        <>
          <div className="mx-auto w-20 h-20 border-[4px] border-black bg-[#FFD100] shadow-[5px_5px_0_0_#000] flex items-center justify-center animate-wobble">
            <Sparkles strokeWidth={3} className="w-10 h-10" />
          </div>
          <h1 className="font-anton uppercase text-4xl mt-6">Verificando pago…</h1>
          <p className="font-outfit text-black/70 mt-1">Tantito, mi rey.</p>
        </>
      )}

      {status === "approved" && (
        <div data-testid="payment-approved">
          <div className="mx-auto w-20 h-20 border-[4px] border-black bg-[#006847] text-white shadow-[5px_5px_0_0_#000] flex items-center justify-center">
            <CheckCircle2 strokeWidth={3} className="w-12 h-12" />
          </div>
          <h1 className="font-anton uppercase text-5xl mt-6">¡A huevo, quedaste!</h1>
          <p className="font-outfit text-black/70 mt-2">
            Ya tienes el pase chingón. Descarga y comparte todo lo que quieras.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              className="nb-btn font-anton uppercase text-xl px-5 py-3 bg-[#CE1126] text-white border-[3px] border-black shadow-[4px_4px_0_0_#000]"
              data-testid="return-home-btn"
            >
              Crear otro sticker
            </Link>
            <Link
              to="/gallery"
              className="nb-btn font-anton uppercase text-xl px-5 py-3 bg-white border-[3px] border-black shadow-[4px_4px_0_0_#000]"
            >
              Ir a mi galería
            </Link>
          </div>
        </div>
      )}

      {status === "pending" && (
        <div data-testid="payment-pending">
          <div className="mx-auto w-20 h-20 border-[4px] border-black bg-[#FFD100] shadow-[5px_5px_0_0_#000] flex items-center justify-center">
            <Clock strokeWidth={3} className="w-12 h-12" />
          </div>
          <h1 className="font-anton uppercase text-4xl mt-6">Pago pendiente</h1>
          <p className="font-outfit text-black/70 mt-2">
            Estamos esperando confirmación de Mercado Pago. Cuando llegue, se te desbloquea solo.
          </p>
          <button
            onClick={() => navigate("/")}
            className="mt-6 nb-btn font-anton uppercase text-lg px-5 py-3 bg-white border-[3px] border-black shadow-[4px_4px_0_0_#000]"
          >
            Volver al inicio
          </button>
        </div>
      )}

      {status === "failure" && (
        <div data-testid="payment-failed">
          <div className="mx-auto w-20 h-20 border-[4px] border-black bg-[#CE1126] text-white shadow-[5px_5px_0_0_#000] flex items-center justify-center">
            <XCircle strokeWidth={3} className="w-12 h-12" />
          </div>
          <h1 className="font-anton uppercase text-4xl mt-6">No se completó el pago</h1>
          <p className="font-outfit text-black/70 mt-2">
            No hay bronca, wey. Puedes intentarlo otra vez desde tu sticker.
          </p>
          <Link
            to="/"
            className="mt-6 inline-block nb-btn font-anton uppercase text-lg px-5 py-3 bg-white border-[3px] border-black shadow-[4px_4px_0_0_#000]"
          >
            Volver al inicio
          </Link>
        </div>
      )}
    </div>
  );
}
